import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import { HelpIcon } from '../HelpIcon/HelpIcon';
import { Popover, popoverAnchorClass } from '../../primitives/Popover/Popover';
import { SelectMenuItem } from '../SelectMenuItem/SelectMenuItem';
import styles from './Select.module.css';

/**
 * Figma: `Select` (96:2941) — "A form control for choosing a single value from a
 * dropdown list. Combines a labeled input trigger with a floating menu of
 * options. Variants: Type (Default, Icon leading, Search) × State (Placeholder,
 * Default, Focused, Open, Disabled)."
 *
 * Only `Type` becomes a prop. Every `State` is real: `Placeholder` and `Default`
 * are whether a value is selected, `Focused` is focus, `Open` is the menu being
 * open, and `Disabled` is the `disabled` prop.
 *
 * `Type=Search` is a text field — typing filters the menu — which the design does
 * not describe. See plan.md.
 */
export type SelectType = 'default' | 'iconLeading' | 'search';

/** Force a state for documentation and visual regression only. */
export type SelectState = 'default' | 'focused';

export type SelectOption = {
  value: string;
  label: string;
  /** Figma's `Supporting text` toggle — the dimmer text after the label. */
  supportingText?: string;
  /**
   * Shown on the trigger when this option is the selected one, for
   * `type="iconLeading"`. It was the row's leading icon too until 2026-09-23,
   * when `_Select menu item` dropped its icon slot for the checkbox — the rows
   * draw no icon now.
   */
  icon?: ReactNode;
  disabled?: boolean;
};

export type SelectProps = {
  options: SelectOption[];
  /** Figma `Type`. `search` makes the trigger a filtering text field. */
  type?: SelectType;
  /**
   * The leading icon for `type="iconLeading"` — Figma's `🔀 Icon swap`. Ignored
   * by the other two types, which have their own or none.
   */
  icon?: ReactNode;
  /** Controlled selection. Omit to let the select own it. */
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /**
   * Lets the control hold more than one option at once — the rows toggle, the
   * menu stays open, and the trigger reads `5 selected` rather than a label.
   *
   * **Not a Figma variant**, but it is what two Figma instances mean. The
   * Settings Configurations frame (2098:5582) overrides a plain `Select`'s
   * value text to `5 selected` on both `Channels` and `Language`; that is a
   * multi-select drawn as a single-select, because the file has no other shell
   * for one. `_Select menu item` already draws a check when `Selected=True`, so
   * the menu needed nothing new — only the trigger's text and the toggling do.
   *
   * Opt-in, and the single-select path is untouched: `value` / `onValueChange`
   * are ignored while this is on, and `values` / `onValuesChange` take over.
   */
  multiple?: boolean;
  /** Controlled selection for `multiple`. Omit to let the select own it. */
  values?: string[];
  defaultValues?: string[];
  onValuesChange?: (values: string[]) => void;
  /**
   * How the trigger reads with `multiple` on. Defaults to Figma's own string,
   * `5 selected`. A caller that wants the names instead passes its own.
   */
  formatSelection?: (count: number) => string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Figma's `Label` toggle. Omit for a bare input. */
  label?: string;
  /** Figma's `↳ Required *` toggle. */
  required?: boolean;
  /**
   * Figma's `↳ Help icon` toggle. The string is the tooltip's text: Figma's
   * instance here is `Help icon` `Tooltip=Top no arrow` (96:2959), so the icon
   * reveals a bubble on hover or focus rather than only carrying a label.
   *
   * Renamed from `helpLabel` when `HelpIcon` landed — it now names what the
   * tooltip *says*, not the icon.
   */
  helpText?: string;
  /** Figma's `Supporting text` on that tooltip — a second, dimmer line. */
  helpSupportingText?: string;
  /** Figma's `Hint text` toggle. */
  hint?: string;
  placeholder?: string;
  /** Shown by `type="search"` when a query matches nothing. Not in Figma. */
  emptyLabel?: string;
  disabled?: boolean;
  /**
   * Accessible name for a select with no visible `label` — Figma's
   * `Label=false`, which is what a select inside a table cell or a toolbar
   * uses. Without it the combobox is announced unnamed. Ignored when `label` is
   * given, which already names it via `aria-labelledby`. Not in Figma.
   */
  'aria-label'?: string;
  /**
   * Which way the menu opens. `bottom` — the default, and what every `Select` in
   * the file draws — always drops below. `auto` drops below unless that would
   * run the menu past the trigger's nearest clipping ancestor while there is
   * more room above, and then opens upward.
   *
   * Not in Figma, which draws the menu open below. Needed by the `Select
   * dropdown` table cell (first used by the Tune sentiment modal, 2037:7616): a
   * `Table` scrolls horizontally, which clips vertically too, so a select in one
   * of the last rows would otherwise open into a box that cuts its menu off.
   * Opt-in so no existing select moves.
   */
  placement?: 'bottom' | 'auto';
  state?: SelectState;
  className?: string;
};

/**
 * The box a descendant's overflow is cut to: the nearest ancestor that does not
 * let content spill, capped to the viewport. `overflow-x: auto` counts — CSS
 * turns the other axis to `auto` too, so a horizontally scrolling table clips
 * vertically, which is the case this exists for.
 */
function clippingBox(el: HTMLElement): { top: number; bottom: number } {
  const viewport = { top: 0, bottom: window.innerHeight };
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(node);
    if (overflowX !== 'visible' || overflowY !== 'visible') {
      const rect = node.getBoundingClientRect();
      return { top: Math.max(rect.top, viewport.top), bottom: Math.min(rect.bottom, viewport.bottom) };
    }
  }
  return viewport;
}

export function Select({
  options,
  type = 'default',
  icon,
  value,
  defaultValue,
  onValueChange,
  multiple = false,
  values,
  defaultValues,
  onValuesChange,
  formatSelection = (count) => `${count} selected`,
  open,
  defaultOpen = false,
  onOpenChange,
  label,
  required = false,
  helpText,
  helpSupportingText,
  hint,
  placeholder = 'Select team member',
  emptyLabel = 'No matches',
  disabled = false,
  'aria-label': ariaLabel,
  placement = 'bottom',
  state,
  className,
}: SelectProps) {
  const labelId = useId();
  const listId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  /** `placement="auto"` only — whether this opening flipped the menu upward. */
  const [openUp, setOpenUp] = useState(false);
  /* Whether the list overflows — the fade at the menu's foot shows only then. */
  const [scrolling, setScrolling] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState<string | undefined>(defaultValue);
  const [uncontrolledValues, setUncontrolledValues] = useState<string[]>(defaultValues ?? []);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);

  const isSearch = type === 'search';
  const currentValue = value ?? uncontrolledValue;
  const isOpen = (open ?? uncontrolledOpen) && !disabled;

  /*
   * `placement="auto"`: open below, measure, and flip if the menu would be cut
   * off. A layout effect, so the flip happens before the first paint of the
   * open menu — a plain effect would show it below for a frame, clipped, then
   * jump. Reset on close, so every opening measures afresh from where the
   * trigger is now.
   */
  useLayoutEffect(() => {
    if (!isOpen) {
      setOpenUp(false);
      return;
    }
    if (placement !== 'auto' || openUp) return;
    const root = rootRef.current;
    const panel = listRef.current?.closest('[role="listbox"]') ?? listRef.current;
    if (!root || !panel) return;

    const box = clippingBox(root);
    /* The combobox itself — the root also spans a label and hint when they are on. */
    const trigger = (root.querySelector('[role="combobox"]') ?? root).getBoundingClientRect();
    const menu = panel.getBoundingClientRect();
    const roomBelow = box.bottom - trigger.bottom;
    const roomAbove = trigger.top - box.top;
    if (menu.bottom > box.bottom && roomAbove > roomBelow) setOpenUp(true);
  }, [isOpen, placement, openUp]);
  const selected = options.find((o) => o.value === currentValue);

  /*
   * `multiple` only. Kept separate from `currentValue` rather than widening it
   * to `string | string[]`, so every existing read of the single value — the
   * chosen row, the keyboard seed, the trigger — stays exactly as it was.
   */
  const currentValues = values ?? uncontrolledValues;
  const hasSelection = multiple ? currentValues.length > 0 : selected !== undefined;
  const isChosen = (option: SelectOption) =>
    multiple ? currentValues.includes(option.value) : option.value === currentValue;

  /*
   * `null` means "not editing" — the field shows the chosen option rather than a
   * query. Focusing clears it to `''` so a fresh search replaces the value, and
   * blurring puts it back to `null` so the value returns.
   */
  const [query, setQuery] = useState<string | null>(null);
  const collapsed = query === null && hasSelection;

  const setValue = useCallback(
    (next: string) => {
      if (value === undefined) setUncontrolledValue(next);
      onValueChange?.(next);
    },
    [value, onValueChange],
  );

  const setValues = useCallback(
    (next: string[]) => {
      if (values === undefined) setUncontrolledValues(next);
      onValuesChange?.(next);
    },
    [values, onValuesChange],
  );

  const setOpen = useCallback(
    (next: boolean) => {
      if (open === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange],
  );

  /* Only the search type filters; the other two always show every option. */
  const visible = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase();
    if (!isSearch || q === '') return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.supportingText ?? '').toLowerCase().includes(q),
    );
  }, [isSearch, options, query]);

  /*
   * The overflow test the custom `ScrollBar` used to report. Figma dropped
   * `_Scroll bar` from the Open menus on 2026-09-21 and the list now scrolls
   * with the platform's own bar (the user's call), so the list measures itself:
   * on open, whenever the rows change, and on resize.
   */
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!isOpen || !list) return;
    const measure = () => setScrolling(list.scrollHeight > list.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [isOpen, visible]);

  /*
   * Keyboard support — not in Figma, which describes appearance only. A listbox
   * that could only be driven by pointer would make the `combobox` role a lie.
   * The trigger keeps focus while the panel is open, so it owns the keys and
   * points `aria-activedescendant` at the row being walked.
   */
  const [activeIndex, setActiveIndex] = useState(-1);
  const selectableIndexes = visible.reduce<number[]>(
    (acc, option, i) => (option.disabled ? acc : [...acc, i]),
    [],
  );

  /*
   * Opening does not seed an active row. Figma's Open state shows the chosen row
   * on the blue `item-surface-active`, and seeding would immediately repaint it
   * with the hover grey. Editing the query resets it, since the rows moved.
   */
  useEffect(() => {
    setActiveIndex(-1);
  }, [isOpen, query]);

  const step = (delta: number) => {
    if (selectableIndexes.length === 0) return;
    if (activeIndex === -1) {
      // The first key press starts from the chosen row, or the end nearest the key.
      /* With `multiple` on there is no single chosen row — start from the first. */
      const seed = multiple ? currentValues[0] : currentValue;
      const fromValue = visible.findIndex((o) => o.value === seed && !o.disabled);
      setActiveIndex(
        fromValue !== -1
          ? fromValue
          : delta > 0
            ? selectableIndexes[0]
            : selectableIndexes[selectableIndexes.length - 1],
      );
      return;
    }
    const pos = selectableIndexes.indexOf(activeIndex);
    const next = (pos + delta + selectableIndexes.length) % selectableIndexes.length;
    setActiveIndex(selectableIndexes[next]);
  };

  const choose = (option: SelectOption) => {
    if (multiple) {
      /*
       * Toggles, and the menu **stays open** — picking five channels should be
       * five clicks, not five clicks and four re-openings. Same behaviour the
       * filter chip panel already has, which is the other multi-select in the
       * app. The query survives too, so a filtered list does not reset under
       * the pointer between two ticks.
       */
      setValues(
        currentValues.includes(option.value)
          ? currentValues.filter((v) => v !== option.value)
          : [...currentValues, option.value],
      );
      return;
    }
    setValue(option.value);
    setOpen(false);
    setQuery(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) setOpen(true);
        else step(event.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Home':
      case 'End':
        if (!isOpen) return;
        event.preventDefault();
        setActiveIndex(
          event.key === 'Home'
            ? (selectableIndexes[0] ?? -1)
            : (selectableIndexes[selectableIndexes.length - 1] ?? -1),
        );
        break;
      case 'Enter':
        if (!isOpen || activeIndex === -1) return;
        event.preventDefault();
        choose(visible[activeIndex]);
        break;
      case 'Escape':
        /*
         * Only when the menu is actually open, and then the key is **consumed**
         * (2026-09-20). A `<dialog>` treats Escape as a close request and that
         * request is the keydown's default action — so a select open inside a
         * modal used to close its menu and the modal with it, losing whatever
         * the user was in the middle of. Preventing the default stops the
         * dialog hearing it; the menu closes and the modal stays. With the menu
         * shut, Escape is not ours and falls through to the dialog, which is
         * how a modal is meant to close.
         *
         * `Popover` closes on Escape too; this also drops the query so the
         * field goes back to showing the chosen option.
         */
        if (!isOpen) return;
        event.preventDefault();
        setQuery(null);
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const inputClasses = [
    styles.input,
    isOpen || state === 'focused' ? styles.focused : null,
    disabled ? styles.disabled : null,
  ]
    .filter(Boolean)
    .join(' ');

  const comboboxProps = {
    role: 'combobox' as const,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? listId : undefined,
    'aria-labelledby': label !== undefined ? labelId : undefined,
    'aria-label': label === undefined ? ariaLabel : undefined,
    'aria-required': required || undefined,
    'aria-activedescendant':
      isOpen && activeIndex !== -1 ? `${listId}-${activeIndex}` : undefined,
  };

  const chosenText = multiple ? (
    hasSelection && <span className={styles.value}>{formatSelection(currentValues.length)}</span>
  ) : (
    selected !== undefined && (
    <>
      <span
        className={styles.value}
      >
        {selected.label}
      </span>
      {selected.supportingText !== undefined && (
        <span className={styles.supporting}>{selected.supportingText}</span>
      )}
    </>
    )
  );

  return (
    <div ref={rootRef} className={[styles.root, className].filter(Boolean).join(' ')}>
      <div className={`${popoverAnchorClass} ${styles.inputWithLabel}`}>
        {label !== undefined && (
          <span className={styles.labelWrapper}>
            <span className={styles.label} id={labelId}>
              {label}
            </span>
            {required && (
              <span className={styles.asterisk} aria-hidden>
                *
              </span>
            )}
            {helpText !== undefined && (
              <HelpIcon
                text={helpText}
                supportingText={helpSupportingText}
                placement="topNoArrow"
              />
            )}
          </span>
        )}

        {isSearch ? (
          /*
           * A text field cannot be a `<button>`, so the trigger is the field
           * itself inside a styled box. Clicking anywhere in the box focuses it,
           * which is what makes the whole 30px row behave like one control.
           */
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard is handled by the composite widget (roving focus / native control); the pointer handler is a convenience. Axe checks run in Storybook and E2E.
          <div
            className={inputClasses}
            onClick={() => {
              if (disabled) return;
              searchRef.current?.focus();
              setOpen(true);
            }}
          >
            <Icon name="search" className={styles.searchIcon} />
            <span className={styles.content}>
              <input
                {...comboboxProps}
                ref={searchRef}
                className={collapsed ? styles.searchFieldCollapsed : styles.searchField}
                type="text"
                value={query ?? ''}
                placeholder={collapsed ? undefined : placeholder}
                disabled={disabled}
                aria-autocomplete="list"
                onFocus={() => setQuery((q) => q ?? '')}
                onBlur={() => setQuery(null)}
                onChange={(event) => {
                  setQuery(event.target.value);
                  if (!isOpen) setOpen(true);
                }}
                onKeyDown={onKeyDown}
              />
              {collapsed && chosenText}
            </span>
          </div>
        ) : (
          <button
            {...comboboxProps}
            className={inputClasses}
            type="button"
            disabled={disabled}
            onClick={() => setOpen(!isOpen)}
            onKeyDown={onKeyDown}
          >
            <span className={styles.content}>
              {type === 'iconLeading' && icon !== undefined && (
                <span className={styles.leadingIcon}>{icon}</span>
              )}
              {hasSelection ? chosenText : <span className={styles.placeholder}>{placeholder}</span>}
            </span>
            <Icon name="chevronDown" className={styles.chevron} />
          </button>
        )}

        <Popover
          open={isOpen}
          onClose={() => setOpen(false)}
          placement={openUp ? 'top-start' : 'bottom-start'}
          role="listbox"
          multiselectable={multiple || undefined}
          id={listId}
          label={label}
          className={styles.menu}
        >
          {/*
            Keeps a click on a row from blurring the search field first — the blur
            would clear the query, re-filter the list and move the row out from
            under the pointer before the click landed.
          */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- keyboard is handled by the composite widget (roving focus / native control); the pointer handler is a convenience. Axe checks run in Storybook and E2E. */}
          <ul
            ref={listRef}
            className={styles.list}
            onMouseDown={(event) => event.preventDefault()}
          >
            {visible.map((option, i) => (
              <SelectMenuItem
                key={option.value}
                id={`${listId}-${i}`}
                label={option.label}
                supportingText={option.supportingText}
                /*
                 * Figma's row `Type`, derived rather than passed: `Multi Select`
                 * draws the leading checkbox and `Single Select` the trailing
                 * tick, and which one is right is exactly what `multiple`
                 * already says.
                 */
                type={multiple ? 'multiSelect' : 'singleSelect'}
                selected={isChosen(option)}
                disabled={option.disabled}
                /* The keyboard-active row shows the same surface as pointer hover. */
                state={i === activeIndex ? 'hover' : undefined}
                onSelect={() => choose(option)}
              />
            ))}
            {visible.length === 0 && <li className={styles.empty}>{emptyLabel}</li>}
          </ul>
          {/*
            Figma `Overlay` — the scrim that holds the menu's bottom edge while
            the rows scroll under it. Decorative, so `aria-hidden`; it is also
            `pointer-events: none`, since it covers the last row.

            Only while the list overflows: the scrim is a scroll affordance, and
            a menu short enough to fit has nothing to fade out. Same flag that
            the filter chip panel follows.
          */}
          {scrolling && <span className={styles.scrim} aria-hidden />}
        </Popover>
      </div>

      {/* Figma's Open variants drop the hint text entirely — the menu takes its place. */}
      {hint !== undefined && !isOpen && <p className={styles.hint}>{hint}</p>}
    </div>
  );
}
